import { ImportStatus } from '../imports'

export type AccountNotificationsState = {
  page: number
  data: Notification[]
  totalPages: number
  templates: NotificationTemplate[]
  stats: {
    unreadCount: number
  }
}

export enum NotificationTemplateType {
  OrderSuccessfullyPlaced = 'OrderSuccessfullyPlaced',
  OrderPartiallyPlaced = 'OrderPartiallyPlaced',
  ErrorDuringOrderProcessing = 'ErrorDuringOrderProcessing',
  ReportIsDelivered = 'ReportIsDelivered',
  CancelledReportRequest = 'CancelledReportRequest',
  ErrorDuringReportDownload = 'ErrorDuringReportDownload',
  CustomerRequestPriceChanged = 'CustomerRequestPriceChanged',
  ValidationFinalizedNotification = 'ValidationFinalizedNotification',
  SearchFinalizedNotification = 'SearchFinalizedNotification',
  CheckProductsFinalizedNotification = 'CheckProductsFinalizedNotification',
  CartCreatedNotification = 'CartCreatedNotification',
  ImportActionError = 'ImportActionError'
}

export enum NotificationType {
  OrderSuccessfullyPlaced = 'OrderSuccessfullyPlaced',
  OrderPartiallyPlaced = 'OrderPartiallyPlaced',
  ErrorDuringOrderProcessing = 'ErrorDuringOrderProcessing',
  ReportIsDelivered = 'ReportIsDelivered',
  CancelledReportRequest = 'CancelledReportRequest',
  ErrorDuringReportDownload = 'ErrorDuringReportDownload',
  CustomerRequestPriceChanged = 'CustomerRequestPriceChanged',
  OrderPaid = 'OrderPaid',
  OrderCompleted = 'OrderCompleted',
  ConsentAccepted = 'ConsentAccepted',
  PaymentAuthorizationFailed = 'PaymentAuthorizationFailed',
  ReportsExportCompleted = 'ReportsExportCompleted',
  ReportsExportFailed = 'ReportsExportFailed',
  OrdersExportCompleted = 'OrdersExportCompleted',
  OrdersExportFailed = 'OrdersExportFailed',
  ImportValidationFinalized = 'ImportValidationFinalized',
  ImportItemsExportCompleted = 'ImportItemsExportCompleted',
  ImportItemsExportFailed = 'ImportItemsExportFailed',
  ImportsExportCompleted = 'ImportsExportCompleted',
  ImportsExportFailed = 'ImportsExportFailed',
  CheckProductsConfirmed = 'CheckProductsConfirmed',
  ImportSearchFinalized = 'ImportSearchFinalized',
  ImportCheckProductsFinalized = 'ImportCheckProductsFinalized',
  ImportCartCreated = 'ImportCartCreated',
  ImportActionError = 'ImportActionError',
  ImportCartItemsExceeded = 'ImportCartItemsExceeded'
}

export type NotificationTemplate =
  | OrderSuccessfullyPlacedTemplate
  | OrderPartiallyPlacedTemplate
  | ErrorDuringOrderProcessingTemplate
  | ReportIsDeliveredTemplate
  | CancelledReportRequestTemplate
  | ErrorDuringReportDownloadTemplate
  | PriceChangeInYourOrderTemplate
  | ImportValidationFinalizedTemplate
  | ImportSearchFinalizedTemplate
  | ImportCheckProductsFinalizedTemplate
  | ImportCartCreatedTemplate
  | ImportActionErrorTemplate

export type OrderSuccessfullyPlacedTemplate = {
  notificationType: NotificationTemplateType.OrderSuccessfullyPlaced
  title: string
  description: string
  orderIdTitle: string
}
export type OrderPartiallyPlacedTemplate = {
  notificationType: NotificationTemplateType.OrderPartiallyPlaced
  title: string
  description: string
  orderIdTitle: string
  description2: string
  reportNameTitle: string
  companyNameTitle: string
  priceTitle: string
  description3: string
}
export type ErrorDuringOrderProcessingTemplate = {
  notificationType: NotificationTemplateType.ErrorDuringOrderProcessing
  title: string
  description: string
  orderIdTitle: string
  reportNameTitle: string
  companyNameTitle: string
  detailsTitle: string
  priceTitle: string
  orderTotalTitle: string
}
export type ReportIsDeliveredTemplate = {
  notificationType: NotificationTemplateType.ReportIsDelivered
  title: string
  description: string
  orderIdTitle: string
  reportNameTitle: string
  companyNameTitle: string
  detailsTitle: string
}
export type CancelledReportRequestTemplate = {
  notificationType: NotificationTemplateType.CancelledReportRequest
  title: string
  description: string
  orderIdTitle: string
  reportNameTitle: string
  companyNameTitle: string
  detailsTitle: string
}
export type ErrorDuringReportDownloadTemplate = {
  notificationType: NotificationTemplateType.ErrorDuringReportDownload
  title: string
  description: string
  orderIdTitle: string
  reportNameTitle: string
  companyNameTitle: string
  detailsTitle: string
}
export type PriceChangeInYourOrderTemplate = {
  notificationType: NotificationTemplateType.CustomerRequestPriceChanged
  title: string
  description: string
  orderIdTitle: string
  reportNameTitle: string
  companyNameTitle: string
  detailsTitle: string
  originalPriceTitle: string
  newPriceTitle: string
  description2: string
}

export type ImportTemplate = {
  title: string
  description: string
  longDescription: string
  importNameTitle: string
  importIdentifierTitle: string
  lastStepTitle: string
}

export type ImportValidationFinalizedTemplate = ImportTemplate & {
  notificationType: NotificationTemplateType.ValidationFinalizedNotification
  cta: string
}

export type ImportSearchFinalizedTemplate = ImportTemplate & {
  notificationType: NotificationTemplateType.SearchFinalizedNotification
  cta: string
}

export type ImportCheckProductsFinalizedTemplate = ImportTemplate & {
  notificationType: NotificationTemplateType.CheckProductsFinalizedNotification
  cta: string
}

export type ImportCartCreatedTemplate = ImportTemplate & {
  notificationType: NotificationTemplateType.CartCreatedNotification
  cartCta: string
  checkoutCta: string
}

export type ImportActionErrorTemplate = ImportTemplate & {
  notificationType: NotificationTemplateType.ImportActionError
  cta: string
}

export type Notification =
  | OrderSuccessfullyPlaced
  | OrderPartiallyPlaced
  | ErrorDuringOrderProcessing
  | ReportIsDelivered
  | CancelledReportRequest
  | ErrorDuringReportDownload
  | PriceChangeInYourOrder
  | ImportValidationFinalized
  | ImportSearchFinalized
  | ImportCheckProductsFinalized
  | ImportCartCreated
  | ImportActionError

type ReportCustomerRequest = {
  reportCode: string
  reportName: string
  companyName: string
}

type NotificationBaseData = {
  id: string
  createdAt: string
  readAt?: string
  orderId: string
}

type NotificationBase<TNotificationType, TData> = {
  type: TNotificationType
} & TData

export type OrderSuccessfullyPlaced = NotificationBase<NotificationType.OrderSuccessfullyPlaced, NotificationBaseData>

export type OrderPartiallyPlaced = NotificationBase<
  NotificationType.OrderPartiallyPlaced,
  NotificationBaseData & {
    customerRequests: (ReportCustomerRequest & {
      netValueFormatted: string
      valueWithTaxFormatted: string
    })[]
  }
>

export type ErrorDuringOrderProcessing = NotificationBase<
  NotificationType.ErrorDuringOrderProcessing,
  NotificationBaseData & {
    totalNetValueFormatted: string
    totalValueWithTaxFormatted: string
    customerRequests: (ReportCustomerRequest & {
      netValueFormatted: string
      valueWithTaxFormatted: string
    })[]
  }
>

export type ReportIsDelivered = NotificationBase<
  NotificationType.ReportIsDelivered,
  NotificationBaseData & {
    customerRequests: ReportCustomerRequest[]
  }
>

export type CancelledReportRequest = NotificationBase<
  NotificationType.CancelledReportRequest,
  NotificationBaseData & {
    customerRequests: ReportCustomerRequest[]
  }
>

export type ErrorDuringReportDownload = NotificationBase<
  NotificationType.ErrorDuringReportDownload,
  NotificationBaseData & {
    customerRequests: ReportCustomerRequest[]
  }
>

export type PriceChangeInYourOrder = NotificationBase<
  NotificationType.CustomerRequestPriceChanged,
  NotificationBaseData & {
    customerRequests: (ReportCustomerRequest & {
      netValueFormatted: string
      valueWithTaxFormatted: string
      latestNetValueFormatted: string
      latestValueWithTaxFormatted: string
    })[]
  }
>

export type Import = NotificationBaseData & {
  importId: string
  import: {
    importName: string
    lastStep: ImportStatus
    processedItems: number
    totalItems: number
  }
}

export type ImportBase<NotificationType> = NotificationBase<NotificationType, Import>

export type ImportValidationFinalized = ImportBase<NotificationType.ImportValidationFinalized>
export type ImportSearchFinalized = ImportBase<NotificationType.ImportSearchFinalized>
export type ImportCheckProductsFinalized = ImportBase<NotificationType.ImportCheckProductsFinalized>
export type ImportCartCreated = ImportBase<NotificationType.ImportCartCreated>
export type ImportActionError = ImportBase<NotificationType.ImportActionError>

export type NotificationsStats = {
  unreadCount: number
}
